import { useState } from "react";
import { inventoryCategoryApi } from "../api";
import { InventoryPageHeader } from "../components/InventoryPageHeader";
import { InventoryError, InventoryLoading } from "../components/InventoryState";
import { useAsyncData, useMutation } from "../hooks";
import type { InventoryCategory, InventoryCategoryCreateRequest } from "../types";

export default function InventoryCategoriesPage() {
  const { data, loading, error, reload } = useAsyncData<InventoryCategory[]>(
    () => inventoryCategoryApi.list(),
    [],
  );

  const [editing, setEditing] = useState<InventoryCategory | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<InventoryCategoryCreateRequest>({ code: "", name: "", description: "", active: true });
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation(async (payload: InventoryCategoryCreateRequest) => {
    if (editing) return inventoryCategoryApi.update(editing.id, payload);
    return inventoryCategoryApi.create(payload);
  });

  function openCreate() {
    setEditing(null);
    setForm({ code: "", name: "", description: "", active: true });
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(cat: InventoryCategory) {
    setEditing(cat);
    setForm({ code: cat.code, name: cat.name, description: cat.description ?? "", active: cat.active });
    setFormError(null);
    setShowForm(true);
  }

  // Live duplicate-code check against the already-loaded category list — case-insensitive,
  // and excluding the category being edited (so editing one without changing its code
  // doesn't flag itself as a duplicate).
  const normalizedCode = form.code.trim().toUpperCase();
  const duplicateCode = normalizedCode.length > 0 && (data ?? []).some(
    (c) => c.code.trim().toUpperCase() === normalizedCode && c.id !== editing?.id,
  );

  async function handleSave() {
    if (!form.code.trim() || !form.name.trim()) return setFormError("Code and name are required.");
    if (duplicateCode) return setFormError(`A category with code "${normalizedCode}" already exists.`);
    setFormError(null);
    try {
      await save.mutate(form);
      setShowForm(false);
      setEditing(null);
      await reload();
    } catch (e: any) {
      setFormError(e?.response?.data?.message ?? e?.message ?? "Save failed.");
    }
  }

  async function toggleActive(cat: InventoryCategory) {
    if (cat.active) await inventoryCategoryApi.deactivate(cat.id);
    else await inventoryCategoryApi.activate(cat.id);
    await reload();
  }

  return (
    <div className="space-y-5">
      <InventoryPageHeader
        title="Item Categories"
        actions={
          <button onClick={openCreate} className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
            + New Category
          </button>
        }
      />

      {loading && <InventoryLoading label="Loading categories..." />}
      {error && <InventoryError message={error} />}

      {showForm && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-slate-900">{editing ? "Edit Category" : "New Category"}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Code <span className="text-red-500">*</span>
              <input
                className={`input ${duplicateCode ? "border-red-400 focus:border-red-500" : ""}`}
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="STAT"
              />
              {duplicateCode && (
                <span className="block text-xs font-semibold text-red-600">
                  A category with code &quot;{normalizedCode}&quot; already exists.
                </span>
              )}
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Name <span className="text-red-500">*</span>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Stationery" />
            </label>
            <div className="md:col-span-2">
              <label className="space-y-1.5 text-sm font-medium text-slate-700">
                Description
                <textarea className="input min-h-20" value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
          </div>
          {formError && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}
          <div className="mt-4 flex gap-2">
            <button onClick={handleSave} disabled={save.loading || duplicateCode}
              className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {save.loading ? "Saving…" : editing ? "Update" : "Create"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-xl border px-4 py-2 text-sm font-semibold">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data ?? []).map((cat) => (
                <tr key={cat.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{cat.code}</td>
                  <td className="px-4 py-3 font-medium">{cat.name}</td>
                  <td className="px-4 py-3 text-slate-500">{cat.description || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cat.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {cat.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(cat)} className="mr-3 text-sm font-semibold text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => toggleActive(cat)} className="text-sm font-semibold text-slate-500 hover:underline">
                      {cat.active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
              {(data ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No categories yet. Create one to organise your items.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
